import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, formatMoney, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Scale, ExternalLink, Image as ImageIcon, X, Plus, CheckCircle2, Download, Search, RefreshCw } from "lucide-react";
import DateRangeFilter from "@/components/DateRangeFilter";
import { API_BASE } from "@/lib/api";
import { ConciliacionIngresos } from "@/components/ConciliacionIngresos";

const stateBadge = (v) => {
  const map = {
    pendiente: "bg-zinc-100 text-zinc-700",
    listo: "bg-emerald-100 text-emerald-800",
    entregado: "bg-emerald-100 text-emerald-800",
    facturado: "bg-blue-100 text-blue-800",
    en_proceso: "bg-yellow-100 text-yellow-800",
    empacado: "bg-blue-100 text-blue-800",
    en_camino: "bg-blue-100 text-blue-800",
  };
  const cls = map[v] || "bg-zinc-100 text-zinc-500";
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>{v || "—"}</span>;
};

export default function Conciliacion() {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [data, setData] = useState({ items: [], count: 0 });
  const [comparativo, setComparativo] = useState({ por_metodo: [], por_banco: [], concs_count: 0 });
  const [metodosPago, setMetodosPago] = useState([]);
  const [galleryOrder, setGalleryOrder] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [modo, setModo] = useState("actual"); // actual | historial
  const [cierres, setCierres] = useState([]);
  const [cierreId, setCierreId] = useState("");
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    api.get("/cierres-mes").then(({ data }) => setCierres(data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      if (modo === "historial") {
        if (cierreId) params.cierre_id = cierreId;
        else params.historial = true;
      }
      const [c, r, mp] = await Promise.all([
        api.get("/reports/conciliacion-pedidos", { params }),
        api.get("/conciliacion-por-metodo/resumen", { params }),
        api.get("/bancos"),
      ]);
      setData(c.data);
      setComparativo(r.data);
      setMetodosPago((mp.data || []).filter((m) => m.activo !== false));
    } catch (e) { toast.error(formatApiError(e)); }
  }, [desde, hasta, modo, cierreId]);
  useEffect(() => { load(); }, [load]);

  const openGallery = async (orderId) => {
    try {
      const { data } = await api.get(`/orders/${orderId}/fabricacion-fotos`);
      setGallery(data);
      setGalleryOrder(orderId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sincronizar = async (orderId) => {
    setSyncing(true);
    try {
      const { data: r } = await api.post("/conciliacion-manual/sincronizar", orderId ? { order_id: orderId } : {});
      toast.success(`Sincronizado: ${r.creados} nuevo(s), ${r.vinculados} vinculado(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSyncing(false);
  };

  const s = q.trim().toLowerCase();
  const sDigits = s.replace(/\D/g, "");
  const items = s
    ? data.items.filter((r) =>
        (r.numero || "").toLowerCase().includes(s) ||
        (r.cliente || "").toLowerCase().includes(s) ||
        (sDigits && `${r.cliente_telefono || ""} ${r.cliente_telefono2 || ""}`.replace(/\D/g, "").includes(sDigits)))
    : data.items;

  return (
    <div className="space-y-6" data-testid="conciliacion-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Cierre</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <Scale className="w-8 h-8" /> Conciliación de Pedidos
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Comparativo Sistema vs. Revisión Física por banco/método + detalle por pedido con soporte para múltiples pagos.
          </p>
        </div>
        <DateRangeFilter desde={desde} hasta={hasta} setDesde={setDesde} setHasta={setHasta} testidPrefix="conc" />
      </header>

      {modo === "actual" && <ConciliacionIngresos />}

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden" data-testid="conc-modo-toggle">
          <button type="button" onClick={() => { setModo("actual"); setCierreId(""); }}
                  className={`px-3 py-2 text-xs uppercase tracking-widest font-black ${modo === "actual" ? "bg-black text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                  data-testid="conc-tab-actual">Actual</button>
          <button type="button" onClick={() => setModo("historial")}
                  className={`px-3 py-2 text-xs uppercase tracking-widest font-black ${modo === "historial" ? "bg-black text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                  data-testid="conc-tab-historial">Conciliaciones Archivadas</button>
        </div>
        {modo === "historial" && (
          <select value={cierreId} onChange={(e) => setCierreId(e.target.value)}
                  className="h-10 border border-zinc-200 rounded-md px-3 text-sm" data-testid="conc-hist-cierre">
            <option value="">Todos los cerrados</option>
            {cierres.map((c) => (
              <option key={c.id} value={c.id}>#{c.numero_cierre} · {c.desde} → {c.hasta}</option>
            ))}
          </select>
        )}
        {modo === "historial" && (
          <span className="text-[10px] uppercase tracking-widest font-black text-zinc-500 bg-amber-100 border border-amber-300 rounded px-2 py-1">Solo lectura</span>
        )}
      </div>

      <ComparativoBanco comparativo={comparativo} desde={desde} hasta={hasta} modo={modo} cierreId={cierreId} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por pedido, cliente o teléfono..." className="pl-9" data-testid="conc-search" />
        </div>
        {modo === "actual" && (
          <Button onClick={() => sincronizar(null)} disabled={syncing} className="gap-1 bg-[#002FA7] hover:bg-[#00227A]" data-testid="conc-sync-all">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> Sincronizar pagos del sistema
          </Button>
        )}
      </div>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pedidos ({items.length}{s ? ` de ${data.count}` : ""})</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Pedido</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Cliente</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold text-right">Monto</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Dirección</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Fecha creación</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Fecha entrega</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Fabricación</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Empaque</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Entrega</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Facturación</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">N° Factura</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold text-right">Facturado</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Banco / Forma pagado</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Conciliación banco</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Fotos</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.length === 0 && (
                <tr><td colSpan={16} className="text-center text-zinc-400 py-10">Sin pedidos.</td></tr>
              )}
              {items.map((r) => (
                <tr key={r.id} className="hover:bg-zinc-50 align-top" data-testid={`conc-row-${r.numero}`}>
                  <td className="px-3 py-3">
                    <Link to={`/pedidos/${r.id}`} className="font-mono text-xs font-semibold text-[#002FA7] hover:underline">{r.numero}</Link>
                  </td>
                  <td className="px-3 py-3 text-xs">
                    {r.cliente}
                    {r.cliente_telefono && <div className="text-[10px] text-zinc-500">{r.cliente_telefono}</div>}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums font-semibold text-blue-700" data-testid={`conc-monto-${r.numero}`}>{formatMoney(r.total)}</td>
                  <td className="px-3 py-3 text-xs text-zinc-600 max-w-[180px] truncate" title={r.direccion}>{r.direccion || "—"}</td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap" data-testid={`conc-fecha-creado-${r.numero}`}>{r.fecha_creado ? formatDate(r.fecha_creado) : "—"}</td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap" data-testid={`conc-fecha-entregado-${r.numero}`}>{r.fecha_entregado ? formatDate(r.fecha_entregado) : <span className="text-zinc-300">—</span>}</td>
                  <td className="px-3 py-3">{stateBadge(r.estado_fabricacion)}</td>
                  <td className="px-3 py-3">{stateBadge(r.estado_empaque)}</td>
                  <td className="px-3 py-3">{stateBadge(r.estado_entrega)}</td>
                  <td className="px-3 py-3">{stateBadge(r.estado_facturacion)}</td>
                  <td className="px-3 py-3 font-mono text-[11px]">{r.facturas_numeros || <span className="text-zinc-300">—</span>}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-emerald-700 font-semibold" data-testid={`conc-facturado-${r.numero}`}>
                    {r.monto_facturado ? formatMoney(r.monto_facturado) : <span className="text-zinc-300">—</span>}
                  </td>
                  {/* Banco / Forma pagado — sistema */}
                  <td className="px-3 py-3">
                    {(r.pagos_detalle || []).length === 0 ? (
                      <span className="text-zinc-300 text-xs">—</span>
                    ) : (
                      <div className="space-y-0.5">
                        {r.pagos_detalle.map((p, i) => (
                          <div key={i} className={`text-[10px] rounded px-1.5 py-0.5 ${p.conciliado ? "bg-emerald-50" : "bg-blue-50"}`} data-testid={`conc-pago-${r.numero}-${i}`}>
                            {p.conciliado && <CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5 text-emerald-700" />}
                            <b>{p.banco || p.metodo_pago || "—"}</b> · Q{p.monto}
                            {p.referencia && <span className="text-zinc-500 font-mono"> · {p.referencia}</span>}
                          </div>
                        ))}
                        {modo === "actual" && r.pagos_detalle.some((p) => !p.conciliado) && (
                          <button onClick={() => sincronizar(r.id)} disabled={syncing} className="text-[9px] text-[#002FA7] hover:underline inline-flex items-center gap-1" data-testid={`conc-sync-${r.numero}`}>
                            <RefreshCw className="w-2.5 h-2.5" /> Sincronizar
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  {/* Conciliación banco — física (editable, múltiples entradas) */}
                  <td className="px-3 py-3">
                    {modo === "historial" ? (
                      <div className="text-[11px] px-2 py-1 min-w-[180px]" data-testid={`conc-readonly-banco-${r.numero}`}>
                        {(r.conciliaciones_manuales || []).length > 0 ? (
                          <div className="space-y-0.5">
                            {r.conciliaciones_manuales.map((c) => (
                              <div key={c.id} className="text-[10px] bg-zinc-100 rounded px-1.5 py-0.5">
                                <b>{c.banco || c.metodo_pago || "—"}</b> · <span className="font-mono">{c.autorizacion}</span> · Q{c.monto}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-zinc-400 text-[10px]">Sin conciliación</span>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditRow(r)}
                        className="text-[11px] text-left w-full hover:bg-emerald-50 rounded px-2 py-1 border border-dashed border-zinc-200 min-w-[180px]"
                        data-testid={`conc-edit-banco-${r.numero}`}
                      >
                        {(r.conciliaciones_manuales || []).length > 0 ? (
                          <div className="space-y-0.5">
                            {r.conciliaciones_manuales.map((c) => (
                              <div key={c.id} className="text-[10px] bg-emerald-50 rounded px-1.5 py-0.5">
                                <b>{c.banco || c.metodo_pago || "—"}</b> · <span className="font-mono">{c.autorizacion}</span> · Q{c.monto}
                              </div>
                            ))}
                            <div className="text-[9px] text-emerald-700 mt-1 flex items-center gap-1"><Plus className="w-2.5 h-2.5" /> Agregar otro</div>
                          </div>
                        ) : (
                          <span className="text-zinc-400 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Agregar</span>
                        )}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {r.fotos_fabricacion_count > 0 ? (
                      <button onClick={() => openGallery(r.id)} className="text-blue-700 hover:underline text-xs inline-flex items-center gap-1" data-testid={`conc-fotos-${r.numero}`}>
                        <ImageIcon className="w-3 h-3" /> {r.fotos_fabricacion_count}
                      </button>
                    ) : <span className="text-zinc-300 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <Link to={`/pedidos/${r.id}`} className="text-[#002FA7]"><ExternalLink className="w-3 h-3" /></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {galleryOrder && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => { setGalleryOrder(null); setGallery([]); }}>
          <div className="relative bg-white rounded-md p-4 max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">Fotos de fabricación</h3>
              <button onClick={() => { setGalleryOrder(null); setGallery([]); }}><X className="w-5 h-5" /></button>
            </div>
            {gallery.length === 0 && <p className="text-zinc-400">Sin fotos.</p>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {gallery.map((f) => (
                <div key={f.id} className="border border-zinc-200 rounded-md p-2">
                  <img src={f.foto_base64} alt="fabricación" className="max-h-48 w-full object-contain rounded" />
                  {f.descripcion && <div className="text-[11px] mt-1 text-zinc-600">{f.descripcion}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {editRow && (
        <EditBancoModal row={editRow} metodosPago={metodosPago} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); load(); }} />
      )}
    </div>
  );
}

// -------- Modal: registrar múltiples pagos por pedido (usa metodos_pago dropdown) --------
function EditBancoModal({ row, metodosPago, onClose, onSaved }) {
  const saldo = Math.max(0, Number(row.total || 0) - (row.conciliaciones_manuales || []).reduce((a, c) => a + Number(c.monto || 0), 0));
  const [form, setForm] = useState({
    // R35: Fecha en zona America/Guatemala (evita que un depósito del día 5
    // a las 10pm GT se guarde como día 6 UTC).
    fecha: new Date().toLocaleDateString("en-CA", { timeZone: "America/Guatemala" }),
    autorizacion: "",
    monto: saldo || Number(row.total || 0),
    banco: "",
    metodo_pago: "",
    concepto: "",
  });
  const save = async () => {
    if (!form.autorizacion.trim()) return toast.error("Autorización requerida");
    if (!form.monto || form.monto <= 0) return toast.error("Monto > 0");
    if (!form.banco.trim() && !form.metodo_pago.trim()) return toast.error("Banco o Método requerido");
    try {
      await api.post("/conciliacion-manual", { ...form, pedido: row.numero, order_id: row.id });
      toast.success("Registrado");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await api.delete(`/conciliacion-manual/${id}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const totalRegistrado = (row.conciliaciones_manuales || []).reduce((a, c) => a + Number(c.monto || 0), 0);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md p-5 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-banco-modal">
        <div className="flex justify-between items-center mb-1">
          <h3 className="font-semibold text-lg">Conciliación de banco — múltiples pagos</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Pedido <b className="font-mono text-[#002FA7]">{row.numero}</b> · {row.cliente} · Total <b>{formatMoney(row.total)}</b>
          {totalRegistrado > 0 && <> · Ya registrado <b className="text-emerald-700">{formatMoney(totalRegistrado)}</b> · Saldo <b className="text-orange-700">{formatMoney(saldo)}</b></>}
        </p>
        {(row.conciliaciones_manuales || []).length > 0 && (
          <div className="mb-4 border border-zinc-200 rounded-md p-2 space-y-1">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pagos registrados</div>
            {row.conciliaciones_manuales.map((c) => (
              <div key={c.id} className="flex justify-between items-center text-xs bg-emerald-50 px-2 py-1 rounded">
                <span className="font-mono">{c.fecha} · {c.banco || c.metodo_pago || "—"} · Auth {c.autorizacion} · Q{c.monto}</span>
                <button onClick={() => del(c.id)} className="text-red-600 text-xs hover:underline">Eliminar</button>
              </div>
            ))}
          </div>
        )}
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Agregar nuevo pago</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-xs">Fecha depósito</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="eb-fecha" /></div>
          <div><Label className="text-xs">Autorización *</Label><Input value={form.autorizacion} onChange={(e) => setForm({ ...form, autorizacion: e.target.value })} data-testid="eb-auth" /></div>
          <div><Label className="text-xs">Monto *</Label><Input type="number" step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: Number(e.target.value) })} data-testid="eb-monto" /></div>
          <div>
            <Label className="text-xs">Banco / Método *</Label>
            {metodosPago.length > 0 ? (
              <Select value={form.banco} onValueChange={(v) => setForm({ ...form, banco: v, metodo_pago: v })}>
                <SelectTrigger data-testid="eb-banco"><SelectValue placeholder="Selecciona banco/método" /></SelectTrigger>
                <SelectContent>
                  {metodosPago.map((m) => (
                    <SelectItem key={m.id} value={m.nombre}>{m.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input value={form.banco} onChange={(e) => setForm({ ...form, banco: e.target.value, metodo_pago: e.target.value })} placeholder="No hay métodos configurados" data-testid="eb-banco" />
            )}
          </div>
          <div className="md:col-span-2"><Label className="text-xs">Concepto (opcional)</Label><Input value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} data-testid="eb-concepto" /></div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="eb-cancel">Cerrar</Button>
          <Button onClick={save} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="eb-save">Agregar pago</Button>
        </div>
      </div>
    </div>
  );
}

// -------- Panel: Comparativo Sistema vs Revisión Física — solo por Banco --------
function ComparativoBanco({ comparativo, desde, hasta, modo, cierreId }) {
  const totSis = comparativo.por_banco.reduce((a, x) => a + x.sistema, 0);
  const totFis = comparativo.por_banco.reduce((a, x) => a + x.fisico, 0);
  const cuadrados = comparativo.por_banco.filter((x) => x.cuadra).length;
  const total = comparativo.por_banco.length;
  const exportar = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
      if (modo === "historial") {
        if (cierreId) params.set("cierre_id", cierreId);
        else params.set("historial", "true");
      }
      const res = await fetch(`${API_BASE}/reports/comparativo-banco.xlsx?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("No se pudo exportar");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `comparativo_banco_${desde || "todo"}_${hasta || ""}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
      toast.success("Exportado");
    } catch (e) { toast.error(String(e)); }
  };
  return (
    <Card className="p-5 border-zinc-200 rounded-md" data-testid="comparativo-banco-metodo">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3 flex items-center gap-2">
        <Scale className="w-3 h-3" /> Comparativo Sistema vs. Revisión Física — por Banco
        <span className="ml-auto text-[10px] normal-case font-normal text-zinc-500">
          {comparativo.concs_count} registro(s) físicos · {cuadrados}/{total} cuadran
        </span>
        <Button onClick={exportar} size="sm" className="ml-2 gap-1 bg-emerald-700 hover:bg-emerald-800 h-7 text-[11px]" data-testid="cmp-banco-export">
          <Download className="w-3 h-3" /> Excel
        </Button>
      </div>
      <ComparativoTable title="Por Banco" rows={comparativo.por_banco} testid="cmp-bancos" />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
        <div className="bg-blue-50 rounded-md p-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Sistema (facturas)</div>
          <div className="font-manrope font-black text-xl text-blue-700">{formatMoney(totSis)}</div>
        </div>
        <div className="bg-emerald-50 rounded-md p-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Físico (revisado)</div>
          <div className="font-manrope font-black text-xl text-emerald-700">{formatMoney(totFis)}</div>
        </div>
        <div className={`rounded-md p-2 ${Math.abs(totSis - totFis) < 0.01 ? "bg-emerald-50" : "bg-orange-50"}`}>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Diferencia</div>
          <div className={`font-manrope font-black text-xl ${Math.abs(totSis - totFis) < 0.01 ? "text-emerald-700" : "text-orange-700"}`}>{formatMoney(totSis - totFis)}</div>
        </div>
      </div>
    </Card>
  );
}

function ComparativoTable({ title, rows, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs font-semibold mb-1">{title}</div>
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-400 py-2">Sin datos.</div>
      ) : (
        <table className="w-full text-sm border border-zinc-100 rounded">
          <thead className="bg-zinc-50 text-zinc-600">
            <tr className="text-left">
              <th className="px-2 py-1 text-[10px] uppercase font-semibold">{title.includes("Método") ? "Método" : "Banco"}</th>
              <th className="px-2 py-1 text-[10px] uppercase font-semibold text-right">Sistema</th>
              <th className="px-2 py-1 text-[10px] uppercase font-semibold text-right">Físico</th>
              <th className="px-2 py-1 text-[10px] uppercase font-semibold text-right">Δ</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((r) => (
              <tr key={r.nombre}>
                <td className="px-2 py-1 text-xs">{r.nombre}</td>
                <td className="px-2 py-1 text-right tabular-nums text-blue-700">{formatMoney(r.sistema)}</td>
                <td className="px-2 py-1 text-right tabular-nums text-emerald-700">{formatMoney(r.fisico)}</td>
                <td className={`px-2 py-1 text-right tabular-nums font-semibold ${r.cuadra ? "text-emerald-700" : "text-orange-700"}`}>
                  {r.cuadra ? <CheckCircle2 className="w-3 h-3 inline" /> : formatMoney(r.diferencia)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
